import { useState } from "react";
import {
  battingHandShort,
  bowlingStyleName,
  bowlingStyleShort,
  formatBowling,
  formatOvers,
} from "@sportsos/engine";
import type { BatterHand, InningsState, MatchState, PlayerId, RuleWarning } from "@sportsos/engine";
import type { SquadPlayer } from "../match/setup.ts";
import { completeBowling } from "../match/styles.ts";
import type { BowlingChoice, KnownBowling } from "../match/styles.ts";
import { BowlingStep, HandStep } from "./StyleSteps.tsx";

/** The button beside a question: "← Back" to the previous pick, or "Cancel" to keep what is there. */
export interface PromptBack {
  readonly label: string;
  readonly onClick: () => void;
}

/** A question's title, with its Back or Cancel beside it rather than under the list. */
function PromptHead({
  back,
  gap = true,
  children,
}: {
  readonly back: PromptBack | undefined;
  readonly gap?: boolean;
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--ds-space-2)", marginBottom: gap ? "var(--ds-space-3)" : 0 }}>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
      {back === undefined ? null : (
        <button className="ds-btn ds-btn--secondary ds-btn--sm" style={{ flexShrink: 0 }} onClick={back.onClick}>
          {back.label}
        </button>
      )}
    </div>
  );
}

/**
 * Who comes in, and which way does he stand (ADR-0023).
 *
 * A player the app already knows is one tap, exactly as before: the tile shows his saved hand
 * ("#4 · LHB") and tapping the name brings him in with it. A player it does not know is asked
 * once, in place on this same sheet, because a hand is never guessed on anyone's behalf. The line
 * under the name is its own target, for the scorer who needs to change what is saved.
 */
export function BatterPrompt({
  takesStrike,
  available,
  innings,
  handOf,
  onPick,
  title,
  subtitle,
  current,
  back,
}: {
  readonly takesStrike: boolean;
  readonly available: readonly SquadPlayer[];
  readonly innings: InningsState;
  /** What is already known about each player's hand, or null. */
  readonly handOf: (playerId: PlayerId) => BatterHand | null;
  readonly onPick: (playerId: PlayerId, hand: BatterHand) => void;
  /** In place of the usual question, e.g. "Change the striker" from the Openers strip. */
  readonly title?: string;
  readonly subtitle?: string;
  /** The player in the slot now, marked so on his tile. */
  readonly current?: PlayerId;
  /** "← Back" while picking the openers, or "Cancel" when changing one. */
  readonly back?: PromptBack | undefined;
}): React.ReactElement {
  const retiredHurt = new Set(
    innings.batting.filter((line) => line.canReturn).map((line) => line.playerId),
  );
  /** The player whose hand is being asked or changed, and nothing else on the sheet while he is. */
  const [asking, setAsking] = useState<SquadPlayer | null>(null);

  if (asking !== null) {
    return (
      <HandStep
        name={asking.name}
        current={handOf(asking.id)}
        onPick={(hand) => {
          setAsking(null);
          onPick(asking.id, hand);
        }}
        onBack={() => setAsking(null)}
      />
    );
  }

  return (
    <div style={{ padding: "var(--ds-space-3)" }}>
      <PromptHead back={back}>
        <div style={{ fontWeight: 700, fontSize: "var(--ds-text-md)" }}>
          {title ?? (takesStrike ? "Who is on strike?" : "Who is at the other end?")}
        </div>
        <div className="ds-caption">
          {available.length === 0 ? "Nobody left to bat" : subtitle ?? "Tap to select"}
        </div>
      </PromptHead>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "var(--ds-space-2)", maxHeight: "45dvh", overflowY: "auto" }}>
        {available.map((player) => {
          const hand = handOf(player.id);
          return (
            <div key={player.id} className="pick-tile">
              <button
                className="pick-tile__name"
                onClick={() => (hand === null ? setAsking(player) : onPick(player.id, hand))}
              >
                <b>{player.name}</b>
                <small>
                  {retiredHurt.has(player.id) ? "returning" : `#${player.battingOrder}`}
                  {player.id === current ? " · now" : ""}
                </small>
              </button>
              <button
                className={`pick-tile__style${hand === null ? " pick-tile__style--unset" : ""}`}
                aria-label={
                  hand === null
                    ? `Set how ${player.name} bats`
                    : `Change how ${player.name} bats, now ${hand}-handed`
                }
                onClick={() => setAsking(player)}
              >
                {hand === null ? "Set hand" : battingHandShort(hand)}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Who bowls, and how (ADR-0023). Shown for every new over, so the type is asked or confirmed at
 * the one moment it can change. A bowler the app knows completely is one tap with his saved
 * style; the line under his name opens the arm and type to change what he bowls from this over.
 */
export function BowlerPrompt({
  overNo,
  candidates,
  innings,
  warningsFor,
  styleOf,
  onPick,
  onClose,
  title,
  subtitle,
  back,
}: {
  readonly overNo: number;
  readonly candidates: readonly SquadPlayer[];
  readonly innings: InningsState;
  readonly warningsFor: (playerId: PlayerId) => readonly RuleWarning[];
  /** What is already known about how each player bowls. */
  readonly styleOf: (playerId: PlayerId) => KnownBowling;
  readonly onPick: (playerId: PlayerId, style: BowlingChoice) => void;
  readonly onClose?: () => void;
  readonly title?: string;
  readonly subtitle?: string;
  /** "← Back" while picking the openers, or "Cancel" when changing the opening bowler. */
  readonly back?: PromptBack | undefined;
}): React.ReactElement {
  const figures = new Map(innings.bowling.map((line) => [line.playerId as string, line]));
  const [asking, setAsking] = useState<SquadPlayer | null>(null);

  const sorted = [...candidates].sort((a, b) => {
    const aWarn = warningsFor(a.id).length;
    const bWarn = warningsFor(b.id).length;
    return aWarn - bWarn;
  });

  if (asking !== null) {
    return (
      <BowlingStep
        name={asking.name}
        current={styleOf(asking.id)}
        doneLabel={`${asking.name} bowls`}
        onDone={(choice) => {
          setAsking(null);
          onPick(asking.id, choice);
        }}
        onBack={() => setAsking(null)}
      />
    );
  }

  return (
    <div style={{ padding: "var(--ds-space-3)", display: "flex", flexDirection: "column", gap: "var(--ds-space-3)" }}>
      <PromptHead back={back} gap={false}>
        <div className="ds-title">{title ?? `Who bowls over ${overNo + 1}?`}</div>
        <div className="ds-caption">{subtitle ?? "Tap to select · greyed out bowlers are unavailable"}</div>
      </PromptHead>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "var(--ds-space-2)", maxHeight: "40dvh", overflowY: "auto" }}>
        {sorted.map((player) => {
          const line = figures.get(player.id);
          const warnings = warningsFor(player.id);
          const blocked = warnings.length > 0;
          const known = styleOf(player.id);
          const complete = completeBowling(known);

          return (
            <div key={player.id} className="pick-tile" aria-disabled={blocked}>
              <button
                className="pick-tile__name"
                disabled={blocked}
                onClick={() => {
                  if (blocked) return;
                  if (complete === null) setAsking(player);
                  else onPick(player.id, complete);
                }}
              >
                <b>{player.name}</b>
                <small>{blocked ? "unavailable" : line === undefined ? "—" : formatBowling(line)}</small>
              </button>
              <button
                className={`pick-tile__style${complete === null ? " pick-tile__style--unset" : ""}`}
                disabled={blocked}
                aria-label={
                  complete === null
                    ? `Set how ${player.name} bowls`
                    : `Change how ${player.name} bowls, now ${bowlingStyleName(known.arm, known.type)}`
                }
                onClick={() => setAsking(player)}
              >
                {complete === null ? "Set style" : bowlingStyleShort(complete.arm, complete.type)}
              </button>
            </div>
          );
        })}
      </div>
      {onClose !== undefined && (
        <button className="ds-btn ds-btn--secondary ds-btn--block" onClick={onClose}>Cancel</button>
      )}
    </div>
  );
}

export function InningsBreak({
  innings,
  teamName,
  chasingName,
  onStart,
}: {
  readonly innings: InningsState;
  readonly teamName: string;
  readonly chasingName: string;
  readonly onStart: () => void;
}): React.ReactElement {
  return (
    <div style={{ padding: "var(--ds-space-4)" }}>
      <div style={{ fontWeight: 700, fontSize: "var(--ds-text-md)", marginBottom: "var(--ds-space-2)" }}>
        Innings break — {teamName} {innings.allOut ? `${innings.runs} all out` : `${innings.runs}/${innings.wickets}`} ({formatOvers(innings.overs)})
      </div>
      <div className="ds-caption" style={{ marginBottom: "var(--ds-space-4)" }}>
        {chasingName} need {innings.runs + 1} to win
      </div>
      <button className="ds-btn ds-btn--primary ds-btn--lg ds-btn--block" onClick={onStart}>
        Start {chasingName}&apos;s innings
      </button>
    </div>
  );
}

export function MatchOver({
  state,
  winnerName,
  loopReport,
  onComplete,
  completing,
  completionError,
  completed,
}: {
  readonly state: MatchState;
  readonly winnerName: string | null;
  readonly loopReport: string | null;
  readonly onComplete: () => void;
  readonly completing: boolean;
  readonly completionError: string | null;
  readonly completed: boolean;
}): React.ReactElement {
  return (
    <div style={{ padding: "var(--ds-space-4)" }}>
      <div style={{ fontWeight: 700, fontSize: "var(--ds-text-md)", marginBottom: "var(--ds-space-2)" }}>
        Match complete
      </div>
      <div style={{ fontWeight: 700, color: "var(--ds-success-600)", marginBottom: "var(--ds-space-2)" }}>
        {winnerName === null ? state.result?.text : `${winnerName} ${state.result?.text}`}
      </div>
      {completed ? (
        <div className="ds-caption">Match finalised. You can close this screen.</div>
      ) : (
        <>
          <div className="ds-caption">Undo is still available if a ball was mis-recorded.</div>
          {completionError !== null && <div className="ds-caption" style={{ color: "var(--ds-danger-600)", marginTop: "var(--ds-space-2)" }}>{completionError}</div>}
        </>
      )}
      {loopReport !== null && (
        <div className="ds-caption" style={{ fontFamily: "var(--ds-font-mono)", fontSize: "var(--ds-text-2xs)", lineHeight: 1.5, userSelect: "text", WebkitUserSelect: "text", marginTop: "var(--ds-space-2)" }} data-testid="loop-report">
          {loopReport}
        </div>
      )}
      {!completed && (
        <button className="ds-btn ds-btn--primary ds-btn--lg ds-btn--block" style={{ marginTop: "var(--ds-space-4)" }} disabled={completing} onClick={onComplete}>
          {completing ? "Finalising…" : "Finalise result"}
        </button>
      )}
    </div>
  );
}

export function SuperOverPrompt({
  runs,
  onStart,
}: {
  readonly runs: number;
  readonly onStart: () => void;
}): React.ReactElement {
  return (
    <div style={{ padding: "var(--ds-space-4)" }}>
      <div style={{ fontWeight: 700, fontSize: "var(--ds-text-md)", marginBottom: "var(--ds-space-2)" }}>Match tied</div>
      <div className="ds-caption" style={{ marginBottom: "var(--ds-space-4)" }}>
        Scores level at {runs} each. Super over next.
      </div>
      <button className="ds-btn ds-btn--primary ds-btn--lg ds-btn--block" onClick={onStart}>
        Start super over
      </button>
    </div>
  );
}

export function SuperOverTiedPrompt({
  onAnother,
  onDeclareTied,
}: {
  readonly onAnother: () => void;
  readonly onDeclareTied: () => void;
}): React.ReactElement {
  return (
    <div style={{ padding: "var(--ds-space-4)" }}>
      <div style={{ fontWeight: 700, fontSize: "var(--ds-text-md)", marginBottom: "var(--ds-space-2)" }}>Super over tied</div>
      <div className="ds-caption" style={{ marginBottom: "var(--ds-space-4)" }}>
        Both sides level again. Another super over, or declare tied?
      </div>
      <div className="row" style={{ gap: "var(--ds-space-2)" }}>
        <button className="ds-btn ds-btn--secondary" style={{ flex: 1 }} onClick={onDeclareTied}>Declare tied</button>
        <button className="ds-btn ds-btn--primary" style={{ flex: 1 }} onClick={onAnother}>Another super over</button>
      </div>
    </div>
  );
}

export function RetireSheet({
  striker,
  nonStriker,
  name,
  onRetire,
  onClose,
}: {
  readonly striker: PlayerId;
  readonly nonStriker: PlayerId;
  readonly name: (playerId: PlayerId) => string;
  readonly onRetire: (playerId: PlayerId, reason: "hurt" | "not_out" | "out") => void;
  readonly onClose: () => void;
}): React.ReactElement {
  return (
    <div style={{ padding: "var(--ds-space-3)" }}>
      <div style={{ fontWeight: 700, fontSize: "var(--ds-text-md)", marginBottom: "var(--ds-space-1)" }}>
        Who is retiring?
      </div>
      <div className="ds-caption" style={{ marginBottom: "var(--ds-space-3)" }}>Retired out counts as a wicket</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "var(--ds-space-2)", maxHeight: "40dvh", overflowY: "auto" }}>
        {[striker, nonStriker].flatMap((playerId) =>
          ([
            ["hurt", "Hurt"],
            ["not_out", "Not out"],
            ["out", "Out (wicket)"],
          ] as const).map(([reason, label]) => (
            <button
              className="ds-key ds-key--sm"
              style={{ height: 52, fontSize: "var(--ds-text-2xs)", fontFamily: "var(--ds-font-sans)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}
              key={`${playerId}-${reason}`}
              onClick={() => onRetire(playerId, reason)}
            >
              <b style={{ fontSize: "var(--ds-text-sm)" }}>{name(playerId)}</b>
              <span>{label}</span>
            </button>
          )),
        )}
      </div>
      <button className="ds-btn ds-btn--secondary ds-btn--block" style={{ marginTop: "var(--ds-space-3)" }} onClick={onClose}>Cancel</button>
    </div>
  );
}
